<script setup lang="ts">
import { isPdfTemplate, PDF_TEMPLATES } from '~/constants/cvTemplates'
import type { CVData, CVTemplate } from '~/types'
import type { CornerDownload } from '~/composables/useCvOffers'
import type { TailorPreview } from '~/utils/cvTailor'
import { useScrollReveal } from '~/composables/useScrollReveal'
import { CV_CHAT_MIN_POSTING } from '~/composables/useCvChat'
import { TAILOR_DEMO_MAX_POSTING } from '~/composables/useTailorDemo'
import { isPostingUrl } from '~/utils/postingUrl'
import { forgetGuestCv, rememberGuestCv, rememberGuestCvTemplate } from '~/composables/useTailorEntry'

/**
 * Ad-landing funnel: paste text → AI CV → watermarked PDF for free, one clean
 * PDF for a single payment, or the Job Search Pack for a clean PDF, a
 * shareable web CV and 30 days of Premium.
 *
 * This page owns the generator and the preview; what the finished CV can be
 * turned into is `LandingResultOffers`, shared with the homepage generator so
 * both end on the same offer.
 *
 * Visually this is the homepage hero (SofaxHeroSection) with the same generator
 * box, so a visitor coming from an ad sees the product they were promised.
 *
 * Like the chat builder, it asks for the job posting while the CV is being
 * written (usePostingDialog), and the result opens with the tailoring to it
 * offered next to the CV, with its price; the CV itself is not tailored.
 */
const props = withDefaults(defineProps<{
  /** Label on the stored guest CV and the signup, so the admin can tell surfaces apart. */
  source?: string
  /** The three-step cards under the generator; the homepage hero leaves them out. */
  withSteps?: boolean
}>(), {
  source: 'free-cv-builder',
  withSteps: true,
})

const { t, locale } = useI18n()
/** Fade-up for the step cards; `refresh()` picks them up again after "start over". */
const { refresh: refreshReveal } = useScrollReveal()
const { trackCvCreated } = useAdsConversion()
/** Decides only how the preview is shown; what a download contains is the server's call. */
const { isPremium } = usePremiumStatus()
const { isAuthenticated } = useAuthUser()

const MIN_CHARS = 50
const MAX_CHARS = 6000

const cvText = ref('')
const isGenerating = ref(false)
const error = ref<string | null>(null)
const generatedCV = ref<CVData | null>(null)
/** Server-side record of this account-less CV; sent with its downloads for the admin stats. */
const generationId = ref<string | null>(null)
const journey = useJourney()

const resultRef = ref<HTMLElement | null>(null)
/** The offers column; the "get the pack" e-mail link lands on it (?focus=offers). */
const offersRef = ref<HTMLElement | null>(null)
/**
 * The CV's review (LandingCvReview), from the day-0 e-mail (?focus=review):
 * the score and what to fix, first thing on the page.
 */
const showReview = ref(false)
const reviewRef = ref<HTMLElement | null>(null)
/** The offers column: the download in the preview's corner runs through it. */
const resultOffers = ref<CornerDownload | null>(null)

/** PDF layouts the visitor can flip between on the result; the choice is saved with the CV. */
const templates = PDF_TEMPLATES satisfies readonly CVTemplate[]
const selectedTemplate = ref<(typeof templates)[number]>('basic')
watch(selectedTemplate, template => rememberGuestCvTemplate(generationId.value, template))

/** The result shows both things the visitor gets: the PDF and the web CV. */
const outputs = [
  { id: 'pdf', icon: 'bi-file-earmark-pdf' },
  { id: 'web', icon: 'bi-globe2' },
] as const
const previewOutput = ref<(typeof outputs)[number]['id']>('pdf')

const charCount = computed(() => cvText.value.length)
const canGenerate = computed(() => cvText.value.trim().length >= MIN_CHARS && !isGenerating.value)
const isTooShort = computed(() => charCount.value > 0 && charCount.value < MIN_CHARS)

/**
 * The three-step reassurance under the generator, one icon per step. The step
 * number is drawn by the card itself, so the translated titles carry no "1.".
 */
const steps = [
  { id: 'paste', icon: 'bi-clipboard-check' },
  { id: 'preview', icon: 'bi-eye' },
  { id: 'download', icon: 'bi-download' },
] as const

const trustPoints = ['noAccount', 'freePdf', 'fast'] as const

/** The posting the CV is for, from the dialog during the build: null until asked, '' when skipped. */
const posting = ref<string | null>(null)

function showResult() {
  nextTick(() => resultRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' }))
}

/** A posting (its text or link) or none; a job title alone is asked again. */
function acceptPosting(text: string | null): boolean {
  const content = (text ?? '').trim().slice(0, TAILOR_DEMO_MAX_POSTING)
  if (content && !isPostingUrl(content) && content.length < CV_CHAT_MIN_POSTING) return false
  posting.value = content
  return true
}

const postingDialog = reactive(usePostingDialog({
  isBuilding: isGenerating,
  accept: acceptPosting,
  onDone: showResult,
}))

async function generate() {
  if (!canGenerate.value) return

  isGenerating.value = true
  error.value = null
  const asks = posting.value === null
  if (asks) postingDialog.startBuild()
  let built = false

  try {
    const response = await $fetch<{ success: boolean; cvData: CVData; generationId?: string | null; sig?: string | null }>('/api/ai/import-cv', {
      method: 'POST',
      // `source` and `locale` are only labels on the stored guest CV, so the
      // admin can tell this landing page apart from the homepage generator.
      body: { text: cvText.value, source: props.source, locale: locale.value, visitorId: journey.visitorId() },
    })

    if (response.success && response.cvData) {
      generatedCV.value = response.cvData
      generationId.value = response.generationId ?? null
      trackCvCreated(generationId.value)
      // The tailoring page offers this CV again (useTailorEntry).
      if (!isAuthenticated.value) rememberGuestCv(response.generationId, response.sig)
      built = true
    }
  } catch (err) {
    const fetchError = err as { statusCode?: number; response?: { status?: number }; data?: { message?: string; statusMessage?: string } }
    const status = fetchError.statusCode ?? fetchError.response?.status
    journey.track('cv_generate_failed', { source: props.source, status: status ?? 0 })
    error.value = status === 429
      ? t('landing.builder.errors.dailyLimit')
      : fetchError.data?.message || fetchError.data?.statusMessage || t('landing.builder.errors.generate')
  } finally {
    isGenerating.value = false
  }
  postingDialog.endBuild(built, asks && posting.value === null)
}

/*
 * Opened from the "e-mail me my CV" link (?draft=<id>&sig=…, see
 * server/utils/guestCvLink.ts): show that CV's result straight away, with the
 * template it was e-mailed in. The day-0 e-mail's tailoring link opens it the
 * same way, with `focus=tailor`.
 */
const route = useRoute()
onMounted(async () => {
  const draft = route.query.draft
  const sig = route.query.sig
  if (typeof draft !== 'string' || typeof sig !== 'string') return
  try {
    const response = await $fetch<{ id: string; cvData: CVData; expiresAt?: string }>(`/api/guest-cv/${encodeURIComponent(draft)}`, { query: { sig } })
    generatedCV.value = response.cvData
    generationId.value = response.id
    if (!isAuthenticated.value) rememberGuestCv(response.id, sig, response.expiresAt)
    const template = route.query.template
    if (isPdfTemplate(template)) selectedTemplate.value = template
    if (route.query.focus === 'interview') {
      scrollToCard('cv-interview')
      return
    }
    if (route.query.focus === 'web') {
      // The web CV e-mail: their CV as a website, the tab the pack publishes.
      previewOutput.value = 'web'
      showResult()
      return
    }
    if (route.query.focus === 'review') {
      // The day-0 e-mail's review: its card opens the result, above the CV.
      showReview.value = true
      nextTick(() => reviewRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' }))
      return
    }
    if (route.query.focus === 'tailor') {
      // From the day-0 e-mail (posting=1): the tailoring they ran, as it was,
      // or the posting they gave, in the field with its price and not run.
      // Otherwise, or when there is neither, the posting field ready.
      const saved = route.query.posting === '1' ? await savedTailoring(draft, sig) : null
      scrollToCard('cv-tailor', () => {
        if (saved?.preview) resultOffers.value?.restoreTailor(saved.preview, saved.posting)
        else if (saved?.posting) resultOffers.value?.offerTailor(saved.posting)
        else resultOffers.value?.openTailor()
      })
      return
    }
    const target = route.query.focus === 'offers' ? offersRef : resultRef
    nextTick(() => target.value?.scrollIntoView({ behavior: 'smooth', block: 'start' }))
  } catch (err) {
    // Gone (past its 30 days): the tailoring page stops leading here.
    if ((err as { statusCode?: number }).statusCode === 404) forgetGuestCv(draft)
    error.value = t('landing.builder.errors.draftExpired')
  }
})

/** The tailoring this CV had (kept with its parked PDF), or the posting given for it; null when neither or on any error. */
async function savedTailoring(id: string, sig: string) {
  try {
    const result = await $fetch<{ posting: string | null, preview: TailorPreview | null }>(`/api/guest-cv/${encodeURIComponent(id)}/tailoring`, { query: { sig } })
    return result.preview || result.posting ? result : null
  } catch {
    return null
  }
}

/**
 * The interview prep card (?focus=interview, from the follow-up e-mail) and
 * the tailoring card (?focus=tailor) live in the lazily loaded offers and wait
 * for the plan check, so they are looked for a few times before giving up and
 * leaving the page at the result.
 */
function scrollToCard(id: string, onFound?: () => void, attempt = 0) {
  const card = document.getElementById(id)
  if (card) {
    card.scrollIntoView({ behavior: 'smooth', block: 'start' })
    onFound?.()
    return
  }
  if (attempt < 20) setTimeout(() => scrollToCard(id, onFound, attempt + 1), 250)
  else resultRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

function startOver() {
  // The offers unmount with the result, and with them any purchase started from
  // it: a new CV is a new purchase, while an old paid token keeps working from
  // its own /download page.
  generatedCV.value = null
  generationId.value = null
  showReview.value = false
  posting.value = null
  error.value = null
  nextTick(() => window.scrollTo({ top: 0, behavior: 'smooth' }))
  refreshReveal()
}

</script>

<template>
  <div>
    <!-- Step 1 — the generator IS the hero, laid out exactly like the homepage one -->
    <section v-if="!generatedCV" class="relative overflow-hidden bg-white dark:bg-gray-950">
      <!-- On the homepage the mode switch sits on top, with the same spacing
           as the other hero modes so it does not move when switching. -->
      <div :class="$slots.switcher ? 'pb-12 pt-3 md:pb-16 md:pt-6 lg:pt-8' : 'pb-12 pt-10 md:py-16 lg:pt-20 xl:pb-20'">
        <div class="shell relative">
          <!-- Decorative elements from the template, hidden on small screens.
               On the homepage (with the switcher) the hero stage draws them
               for all modes instead. -->
          <template v-if="!$slots.switcher">
            <img
              src="/sofax/hero-1-element-1.svg"
              alt=""
              aria-hidden="true"
              width="291"
              height="200"
              class="pointer-events-none absolute -left-16 top-1/2 -z-10 hidden opacity-70 xl:inline-block"
            >
            <img
              src="/sofax/hero-1-element-2.svg"
              alt=""
              aria-hidden="true"
              width="311"
              height="243"
              class="pointer-events-none absolute -right-24 top-0 -z-10 hidden opacity-70 2xl:inline-block"
            >
          </template>

          <div v-if="$slots.switcher" class="mb-4 md:mb-6">
            <slot name="switcher" />
          </div>

          <div class="mx-auto max-w-5xl text-center">
            <!-- On the homepage the three modes go without a badge, the headline says it already -->
            <div v-if="!$slots.switcher" class="eyebrow mb-6">
              <i class="bi bi-stars text-accent" />
              <span>{{ t('landing.builder.badge') }}</span>
            </div>

            <!-- Homepage phones: the same one-line headline as the chat mode -->
            <h1 :class="$slots.switcher ? 'hero-mode-title' : 'mb-6'">{{ t('landing.builder.title') }}</h1>

            <p class="lead mx-auto max-w-3xl" :class="{ 'max-md:hidden': $slots.switcher }">{{ t('landing.builder.subtitle') }}</p>

            <form
              id="ai-generator"
              class="mx-auto max-w-[46rem] text-left" :class="$slots.switcher ? 'mt-3 md:mt-6' : 'mt-10 md:mt-12'"
              novalidate
              @submit.prevent="generate"
            >
              <div
                class="rounded-[0.625rem] border border-line bg-white shadow-lift transition-colors focus-within:border-accent dark:border-gray-700 dark:bg-gray-800 dark:focus-within:border-accent"
              >
                <label for="cv-text" class="sr-only">{{ t('landing.builder.inputLabel') }}</label>
                <textarea
                  id="cv-text"
                  v-model="cvText"
                  :placeholder="t('landing.builder.placeholder')"
                  :disabled="isGenerating"
                  :maxlength="MAX_CHARS"
                  rows="8"
                  class="block h-36 w-full resize-y rounded-t-[0.625rem] border-0 bg-transparent p-5 text-base leading-relaxed text-ink placeholder:text-muted focus:outline-none focus:ring-0 disabled:opacity-60 sm:h-auto dark:text-white dark:placeholder:text-gray-500"
                />

                <div class="flex flex-col gap-4 border-t border-line px-5 py-4 sm:flex-row sm:items-center sm:justify-between dark:border-gray-700">
                  <div class="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted dark:text-gray-400">
                    <span :class="{ 'text-amber-600 dark:text-amber-400': isTooShort }">
                      {{ isTooShort ? t('landing.builder.minLength', { n: MIN_CHARS }) : `${charCount}/${MAX_CHARS}` }}
                    </span>
                    <button
                      v-if="cvText"
                      type="button"
                      :disabled="isGenerating"
                      class="link disabled:opacity-50"
                      @click="cvText = ''"
                    >
                      {{ t('homepage.hero.input.clear') }}
                    </button>
                  </div>

                  <SofaxBtn
                    type="submit"
                    variant="accent"
                    :disabled="!canGenerate"
                    :label="isGenerating ? t('landing.builder.generating') : t('landing.builder.cta')"
                    class="w-full sm:w-auto"
                  />
                </div>
              </div>

              <SofaxNotice v-if="error" type="error" class="mt-4">{{ error }}</SofaxNotice>

              <!-- Trust line, in place of the template's star rating -->
              <ul class="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-body dark:text-gray-300">
                <li v-for="point in trustPoints" :key="point" class="inline-flex items-center gap-2">
                  <i class="bi bi-check-circle-fill text-accent" />
                  {{ t(`landing.builder.trust.${point}`) }}
                </li>
              </ul>
            </form>
          </div>

          <!--
            Three-step reassurance, kept short on purpose. An ordered list, so
            the sequence survives without the drawn numerals; the hand-drawn
            arrows between the cards are the template's own elements and sit in
            their own grid tracks, which is why they never overlap a card.
          -->
          <ol v-if="withSteps" class="mx-auto mt-14 grid max-w-5xl list-none gap-6 md:mt-20 md:grid-cols-[1fr_auto_1fr_auto_1fr] md:gap-4 lg:gap-6">
            <template v-for="(step, index) in steps" :key="step.id">
              <li v-if="index" aria-hidden="true" class="hidden self-center md:block" data-reveal :style="{ '--reveal-delay': `${index * 140 - 70}ms` }">
                <img
                  src="/sofax/hero-1-element-1.svg"
                  alt=""
                  width="291"
                  height="200"
                  loading="lazy"
                  class="pointer-events-none w-12 select-none lg:w-16"
                  :class="index === 2 ? '-rotate-6' : 'rotate-3'"
                >
              </li>

              <li class="hover-shadow group relative" data-reveal :style="{ '--reveal-delay': `${index * 140}ms` }">
                <div class="card flex h-full flex-col items-start gap-5 text-left">
                  <!-- Ghost numeral: the step order as a graphic element, picked up by the card's hover -->
                  <div
                    aria-hidden="true"
                    class="pointer-events-none absolute right-6 top-4 font-title text-[2.75rem] font-bold leading-none text-line transition-colors duration-300 group-hover:text-accent-soft dark:text-gray-700 dark:group-hover:text-accent"
                  >
                    {{ String(index + 1).padStart(2, '0') }}
                  </div>

                  <SofaxIconTile :icon="step.icon" size="md" interactive />

                  <div>
                    <div class="display-heading display-heading-4 mb-2">{{ t(`landing.builder.steps.${step.id}.title`) }}</div>
                    <p class="text-base">{{ t(`landing.builder.steps.${step.id}.description`) }}</p>
                  </div>
                </div>
              </li>
            </template>
          </ol>
        </div>
      </div>
    </section>

    <!-- Step 2 — result: preview plus the two ways forward -->
    <!-- Tight vertical rhythm on purpose: the result has to start as close to the
         top of the viewport as possible, or on a phone neither the CV nor the
         offers below it are in reach. -->
    <section v-else ref="resultRef" class="surface-alt scroll-mt-0">
      <div class="py-6 md:py-10 xl:py-14">
        <div class="shell">
          <div class="mx-auto max-w-6xl">
            <div class="mb-5 text-center md:mb-7">
              <h2 class="!text-2xl md:!text-4xl">{{ t('landing.builder.result.title') }}</h2>
              <p class="mt-2 !text-base md:mt-3 md:!text-lg">{{ t('landing.builder.result.subtitle') }}</p>
            </div>

            <!-- From the day-0 e-mail: the review it promised, before the CV itself -->
            <div v-if="showReview" ref="reviewRef" class="mb-6 scroll-mt-4 md:mb-8">
              <LandingCvReview
                :cv-data="generatedCV"
                :limited="resultOffers ? resultOffers.canBuyPack : true"
                :ready="!!resultOffers"
                :is-buying="!!resultOffers?.buyingPack"
                @buy="resultOffers?.buyPack()"
                @edit="resultOffers?.editCv()"
                @tailor="scrollToCard('cv-tailor', () => resultOffers?.openTailor())"
              />
            </div>

            <div class="grid gap-6 lg:grid-cols-[1.15fr_.85fr] lg:gap-8">
              <!-- Preview: the same CV as the free, watermarked PDF, or as the web CV the pack publishes -->
              <div class="min-w-0">
                <div
                  role="tablist"
                  :aria-label="t('landing.builder.result.outputLabel')"
                  class="mb-3 flex w-full rounded-full border border-line bg-white p-1 dark:border-gray-700 dark:bg-gray-800"
                >
                  <button
                    v-for="output in outputs"
                    :key="output.id"
                    type="button"
                    role="tab"
                    :aria-selected="previewOutput === output.id"
                    class="inline-flex flex-1 items-center justify-center gap-2 rounded-full px-4 py-2 text-base font-semibold transition-colors duration-300"
                    :class="previewOutput === output.id
                      ? 'bg-accent text-white'
                      : 'text-body hover:text-ink dark:text-gray-300 dark:hover:text-white'"
                    @click="previewOutput = output.id"
                  >
                    <i class="bi" :class="output.icon" />
                    {{ t(`landing.builder.result.outputs.${output.id}`) }}
                    <!-- The web CV comes with the pack: say so on the tab, not only in the hint under it -->
                    <span
                      v-if="output.id === 'web'"
                      class="rounded-full bg-lilac px-1.5 py-0.5 !text-[0.625rem] font-bold !leading-tight !text-ink"
                    >
                      {{ t('cvChat.templates.premium') }}
                    </span>
                  </button>
                </div>

                <LandingTemplatePicker v-if="previewOutput === 'pdf'" v-model="selectedTemplate" />
                <p v-else class="mb-4 text-center !text-sm text-muted dark:text-gray-400">
                  {{ t('landing.builder.result.webHint') }}
                </p>

                <!-- Every page stacked on a grey desk, at full height. It used to be
                     capped to ~a screen on phones with an inner scroll, but the touch
                     lands in the preview's iframe there, so the box could not be
                     scrolled and the end of the CV stayed hidden. -->
                <div
                  v-show="previewOutput === 'pdf'"
                  class="relative overflow-hidden rounded-[0.625rem] bg-gray-100 dark:bg-gray-900"
                >
                  <LandingPreviewDownload v-if="resultOffers" :label="resultOffers.cornerLabel" :busy="resultOffers.cornerBusy" :paid="resultOffers.cornerPaid" @click="resultOffers.cornerDownload()" />
                  <LazyCVPreviewWrapper
                    :cv-data="generatedCV"
                    :experiences-list="generatedCV.experiences || []"
                    :education-list="generatedCV.education || []"
                    :show-q-r-code="false"
                    :template="selectedTemplate"
                    frame="border"
                    :watermark="!isPremium"
                  />
                </div>
                <!-- Watermarked like the PDF next to it, and clean for the same visitors. -->
                <div v-if="previewOutput === 'web'" class="relative">
                  <LandingPreviewDownload v-if="resultOffers" :label="resultOffers.websiteLabel" :busy="resultOffers.cornerBusy" paid icon="bi-globe2" @click="resultOffers.cornerWebsite()" />
                  <LazyLandingWebCvPreview :cv-data="generatedCV" />
                </div>
              </div>

              <!-- Offers run most-valuable first: the pack sits at the top of the
                   column, the free download closes it as the fallback. The same
                   column ends the homepage generator. -->
              <div ref="offersRef" class="scroll-mt-24 space-y-4 text-left">
                <LazyLandingResultOffers
                  ref="resultOffers"
                  :cv-data="generatedCV"
                  :template="selectedTemplate"
                  :generation-id="generationId"
                  :signup-source="source"
                  :initial-posting="posting || null"
                  job-match
                  @pick-template="selectedTemplate = $event"
                >
                  <template #links>
                    <button type="button" class="link" @click="startOver">
                      {{ t('landing.builder.startOver') }}
                    </button>
                  </template>
                </LazyLandingResultOffers>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- While the CV is being written: which job is it for? -->
    <LandingPostingDialog :dialog="postingDialog" :building="isGenerating" />
  </div>
</template>

